// audit.ts — the Audit Trail screen (#/audit).
//
// Its own page with its own nav entry, split out of the old combined admin
// screen. The job here is READABILITY: the previous viewer printed a bare
// actor uuid, a raw ISO timestamp and `JSON.stringify(detail)` into a table
// cell, which is technically the whole truth and practically unreadable.
//
// So: the actor is a face and a name (joined server-side), the action is a
// pill coloured by family, the time reads relatively and resolves on hover,
// and `detail` is summarised to the two or three fields that matter with the
// full record one click away. Nothing is hidden — the full JSON is always
// reachable — but the default view answers "what happened here" at a glance.

import type { ColDef, GridApi } from 'ag-grid-community';
import {
  absoluteTime, avatarNode, el, jsonFetch, makeGrid,
  relativeTime, renderRefusal, timeCell,
} from './admin-ui.js';
import { adminGridMachinery } from './admin-grid.js';
import { gridRowsToCards, renderCardList, watchPhone } from './card-list.js';
import { ADMIN_GRID_TOOLBAR, renderGridToolbarButtons } from './grid-toolbar.js';
import { displayValueFromDefs } from './grid-machinery.js';
import { fmtWhen } from './home.js';
import { destroyDialog, openDialog } from './dialog.js';
import { mountShell } from './shell.js';
import { mkIconBtn } from './buttons.js';
import { mkSearchBox } from './search.js';
import { mkFcSelect, type FcOption, type FcSelectCtl } from './form-controls.js';
import type { CtxMenuItem } from './ctxmenu.js';

export interface AuditRow {
  id: number;
  at: string;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_picture_url: string | null;
  action: string;
  subject_type: string | null;
  subject_id: string | null;
  detail: unknown;
  ip: string | null;
}

/** Action families drive the pill colour. Derived from the prefix so a new
 * action added server-side is coloured without touching this list — an
 * unknown family falls back to neutral rather than to no pill at all. */
export function actionFamily(action: string): string {
  const head = action.split('.')[0] ?? '';
  switch (head) {
    case 'login': return action.includes('refused') || action.includes('uninvited') ? 'deny' : 'auth';
    case 'logout': return 'auth';
    case 'csrf': return 'deny';
    case 'user': return action.includes('denied') || action.includes('refused') ? 'deny' : 'user';
    case 'invite': return 'user';
    case 'stakeholder': return 'user';
    case 'release': case 'cutover': return 'release';
    case 'deploy': case 'deployment': return 'deploy';
    case 'service_token': return 'auth';
    default: return 'neutral';
  }
}

/** The one-line human summary of a detail blob. Exported so the headless
 * test can pin it without a DOM. */
export function summariseDetail(row: AuditRow): string {
  const d = row.detail;
  if (d === null || d === undefined) return '';
  if (typeof d !== 'object') return String(d);
  const o = d as Record<string, unknown>;
  const parts: string[] = [];
  // Fields worth surfacing first, in the order a human asks about them.
  for (const k of ['email', 'role', 'reason', 'emailed', 'sessionsDestroyed', 'socketsSevered', 'stakeholderSeats']) {
    if (o[k] !== undefined && o[k] !== null) parts.push(`${k} ${String(o[k])}`);
  }
  if (parts.length === 0) {
    for (const [k, v] of Object.entries(o).slice(0, 3)) {
      if (v !== null && typeof v !== 'object') parts.push(`${k} ${String(v)}`);
    }
  }
  return parts.join(' · ');
}

// ---------------------------------------------------------------------
// R59a — the grid machinery's per-page seats (users-audit-parity).
// ---------------------------------------------------------------------

/** Canonical column order — what reconcileColumnState re-homes a stale
 * saved preset against. MUST match buildAuditColumns's colIds (the headless
 * test pins both against each other). */
export const AUDIT_COLUMN_IDS = ['at', 'actor', 'action', 'subject', 'detail', 'ip'] as const;

/** No shipped pins — the same R59a decision as the Users grid (stated in
 * the seam PR): a viewport-fitting grid gains nothing from a default pin.
 * The seat's three states (absent/[]/list) are unchanged. */
export const DEFAULT_AUDIT_PINS: readonly string[] = [];

/** The eye's vocabulary for one cell (R59a). Two overrides: the datetime
 * column speaks fmtWhen (home.ts's trade — a ticking "3m ago" is useless as
 * a filter value), and an actor-less row speaks the cell's own honest
 * 'System / anonymous', never the valueGetter's machine word. Everything
 * else — action strings, subject types, detail summaries, IPs — already
 * reads in the cell exactly what the valueGetter says. Exported pure for
 * the headless test. */
export function auditDisplayValue(
  defs: ReadonlyArray<ColDef<AuditRow>>,
  colId: string,
  row: AuditRow,
): string {
  switch (colId) {
    case 'at': {
      const s = fmtWhen(row.at);
      return s === '—' ? '' : s;
    }
    case 'actor':
      return row.actor_user_id
        ? (row.actor_email ?? row.actor_name ?? 'unknown')
        : 'System / anonymous';
    default:
      return displayValueFromDefs(defs, colId, row);
  }
}

export function buildAuditColumns(onOpen: (row: AuditRow) => void): ColDef<AuditRow>[] {
  return [
    {
      colId: 'at', headerName: 'When', width: 150, sort: 'desc',
      comparator: (_a, _b, na, nb) =>
        Date.parse(na.data?.at ?? '') - Date.parse(nb.data?.at ?? ''),
      valueGetter: (p) => p.data?.at ?? '',
      cellRenderer: (p: { data?: AuditRow }) => timeCell(p.data?.at ?? null),
    },
    {
      colId: 'actor', headerName: 'Actor', flex: 1.4, minWidth: 210,
      valueGetter: (p) => p.data?.actor_email ?? (p.data?.actor_user_id ? 'unknown' : 'system'),
      cellRenderer: (p: { data?: AuditRow }) => {
        const wrap = el('span', 'hc-person');
        if (!p.data) return wrap;
        // No actor is not a gap — unauthenticated refusals are recorded with
        // no actor ON PURPOSE, and saying "System" is the honest rendering.
        if (!p.data.actor_user_id) {
          wrap.appendChild(el('span', 'hc-avatar hc-avatar-initials hc-avatar-system', '·'));
          wrap.appendChild(el('span', 'hc-person-text', 'System / anonymous'));
          return wrap;
        }
        wrap.appendChild(avatarNode(
          p.data.actor_picture_url, p.data.actor_name, p.data.actor_email ?? '?'));
        const text = el('span', 'hc-person-text');
        text.appendChild(el('span', 'hc-person-name',
          p.data.actor_name ?? p.data.actor_email ?? p.data.actor_user_id));
        if (p.data.actor_email) text.appendChild(el('span', 'hc-person-mail', p.data.actor_email));
        wrap.appendChild(text);
        return wrap;
      },
    },
    {
      colId: 'action', headerName: 'Action', flex: 1, minWidth: 170,
      valueGetter: (p) => p.data?.action ?? '',
      cellRenderer: (p: { data?: AuditRow }) => {
        const e = document.createElement('span');
        if (!p.data) return e;
        e.className = `hc-pill hc-pill-act-${actionFamily(p.data.action)}`;
        e.textContent = p.data.action;
        return e;
      },
    },
    {
      colId: 'subject', headerName: 'Subject', flex: 1, minWidth: 150,
      valueGetter: (p) => (p.data?.subject_type ? `${p.data.subject_type}` : ''),
      cellRenderer: (p: { data?: AuditRow }) => {
        const wrap = el('span', 'hc-subject');
        if (!p.data?.subject_type) return wrap;
        wrap.appendChild(el('span', 'hc-subject-type', p.data.subject_type));
        if (p.data.subject_id) {
          const id = el('span', 'hc-subject-id', p.data.subject_id.slice(0, 8));
          id.dataset.tip = p.data.subject_id;
          wrap.appendChild(id);
        }
        return wrap;
      },
    },
    {
      colId: 'detail', headerName: 'Detail', flex: 2, minWidth: 240, sortable: false,
      valueGetter: (p) => (p.data ? summariseDetail(p.data) : ''),
      cellRenderer: (p: { data?: AuditRow }) => {
        const wrap = el('span', 'hc-detailcell');
        if (!p.data) return wrap;
        const summary = summariseDetail(p.data);
        wrap.appendChild(el('span', 'hc-detail-summary', summary || '—'));
        if (p.data.detail !== null && p.data.detail !== undefined) {
          // Item F: an icon button like the other grids' — the old text is
          // the aria-label AND the data-tip (never the accessible name's
          // replacement, never native title).
          // Styles × Themes PR-2: on the primitive — hc-rowbtn's hand-picked
          // ghost paint is the neutral ramp, and hc-rowact carries only the
          // inter-button gap the row idiom needs.
          const more = mkIconBtn({ glyph: 'eye', label: 'View', size: 'sm', cls: 'hc-rowact' });
          more.addEventListener('click', () => onOpen(p.data!));
          wrap.appendChild(more);
        }
        return wrap;
      },
    },
    {
      colId: 'ip', headerName: 'IP', width: 130,
      valueGetter: (p) => p.data?.ip ?? '',
    },
  ];
}

// ---------------------------------------------------------------------
// bridge-ui-polish (owner item E) — the row context menu's decision, pure.
// ---------------------------------------------------------------------

export interface AuditRowMenuEntry {
  id: 'view';
  label: string;
  icon: string;
  enabled: boolean;
  tip?: string;
}

/** An audit row's one verb: open the full record. Detail-less rows keep
 * the entry DISABLED with the why (stable-roster doctrine); the machinery
 * appends the generic Copy Value after it. */
export function auditRowMenuSpec(row: AuditRow): AuditRowMenuEntry[] {
  const has = row.detail !== null && row.detail !== undefined;
  return [{
    id: 'view', label: 'View Details…', icon: 'eye', enabled: has,
    ...(has ? {} : { tip: 'This record carries no detail payload' }),
  }];
}

export function mountAudit(root: HTMLElement): () => void {
  // The ONE shell (shared-shell, R60/R63) — same chrome as home and #/users;
  // the "200 rows (capped)" count renders in this page's own pagebar row
  // (R68). The returned teardown is the shell's; the router must call it.
  const shell = mountShell(root, 'audit');
  shell.pagebar.innerHTML = `
    <h1 class="hc-pagetitle">Audit trail</h1>
    <div class="hc-status" id="hcAdminStatus"></div>`;
  shell.main.classList.add('hc-admin');
  shell.main.innerHTML = `
    <div class="hc-toolbar" id="hcToolbar"></div>
    <div id="hcGrid" class="hc-grid"></div>
    <div id="hcCardList" class="hc-cardlist"></div>`;
  const status = root.querySelector('#hcAdminStatus') as HTMLElement;
  const toolbar = root.querySelector('#hcToolbar') as HTMLElement;
  const cardListEl = root.querySelector('#hcCardList') as HTMLElement;
  const modal = shell.modal;
  let grid: GridApi<AuditRow> | null = null;
  let rows: AuditRow[] = [];
  /** The defs the live grid was built with (home.ts's currentDefs shape). */
  let currentDefs: ColDef<AuditRow>[] = [];
  let machTeardown: (() => void) | null = null;

  // R59a — the release grid's machinery on THIS page's seat: filters, pins
  // and Copy over whatever ColDefs exist, persisted in ui_state
  // grid-preset/'audit' (the EXISTING keyed kind — no server change). The
  // query selects above the grid stay SERVER-side (which rows are fetched);
  // the column filters are client-side within that window — two layers,
  // not two copies of one.
  const mach = adminGridMachinery<AuditRow>({
    presetKey: 'audit',
    defaultPins: DEFAULT_AUDIT_PINS,
    columnIds: AUDIT_COLUMN_IDS,
    displayValue: (c, r) => auditDisplayValue(currentDefs, c, r),
    rows: () => rows,
    // Item E — View Details… above the machinery's generic Copy Value,
    // running the SAME dialog the cell's eye button opens.
    rowMenu: (r) => auditRowMenuSpec(r).map((s2): CtxMenuItem => ({
      label: s2.label,
      icon: s2.icon,
      disabled: !s2.enabled,
      ...(s2.tip ? { tip: s2.tip } : {}),
      run: () => detailDialog(r),
    })),
  });

  // R59b — Columns/Reset/Refresh from THE grid-toolbar roster. The page's
  // old #hcAuditRefresh text button (the Bridge's only Refresh before R32)
  // is RECONCILED into the roster's #hcRefresh icon button — one Refresh,
  // one id vocabulary across all three grid pages, never two buttons for
  // one verb. No Create here: an audit trail is written by the system, and
  // ruling Q15's default puts no create verb on this page.
  toolbar.innerHTML = `
    <span class="hc-spacer"></span>
    ${renderGridToolbarButtons({
      include: ADMIN_GRID_TOOLBAR,
      tips: { hcRefresh: 'Refresh the audit trail now' },
    })}`;

  // PR-3a — the three filter pickers were native <select>s wearing a themed
  // .hc-select box, which the file's own R69 note already conceded could not
  // theme the OPEN list (that popup is OS chrome). They are the form-control
  // primitive now, keeping their ids so the e2e spec and the muscle memory
  // that binds them survive.
  let actionOpts: FcOption[] = [{ value: '', label: 'All actions' }];
  const actionSel: FcSelectCtl = mkFcSelect({
    id: 'hcAuditAction', label: 'Filter by action', cls: 'hc-select',
    options: () => actionOpts, value: '', onPick: () => void load(),
  });
  const sinceSel: FcSelectCtl = mkFcSelect({
    id: 'hcAuditSince', label: 'Filter by time window', cls: 'hc-select',
    options: [
      { value: '', label: 'Any time' },
      { value: '1', label: 'Last hour' },
      { value: '24', label: 'Last 24 hours' },
      { value: '168', label: 'Last 7 days' },
      { value: '720', label: 'Last 30 days' },
    ],
    value: '24', onPick: () => void load(),
  });
  const limitSel: FcSelectCtl = mkFcSelect({
    id: 'hcAuditLimit', label: 'Rows to load', cls: 'hc-select',
    options: [
      { value: '200', label: '200 rows' },
      { value: '500', label: '500 rows' },
      { value: '1000', label: '1000 rows' },
    ],
    value: '200', onPick: () => void load(),
  });
  toolbar.prepend(actionSel.el, sinceSel.el, limitSel.el);

  // Item D — the shared search component, leftmost above the grid; the
  // INPUT keeps id hcAuditQ (the e2e spec and muscle memory bind it). The
  // filter semantics are unchanged: a debounced SERVER-side `q` — the
  // component owns the box, never what matching means.
  let debounce: number | null = null;
  const searchBox = mkSearchBox({
    id: 'hcAuditQ',
    placeholder: 'Search action, actor or subject',
    label: 'Search the audit trail',
    onInput: () => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void load(), 250);
    },
  });
  toolbar.prepend(searchBox.el);
  const q = searchBox.input;
  // R32's console grammar: the spin rides the in-flight window; a click
  // while spinning is refused — the spin IS the acknowledgment.
  const refreshBtn = toolbar.querySelector('#hcRefresh') as HTMLButtonElement;
  refreshBtn.addEventListener('click', () => {
    if (refreshBtn.classList.contains('hc-spinning')) return;
    refreshBtn.classList.add('hc-spinning');
    void load().finally(() => refreshBtn.classList.remove('hc-spinning'));
  });

  function detailDialog(row: AuditRow): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', row.action));
      const meta = el('p', 'hc-status');
      meta.textContent =
        `${absoluteTime(row.at)} · ${relativeTime(row.at)}` +
        (row.actor_email ? ` · ${row.actor_email}` : ' · system') +
        (row.ip ? ` · ${row.ip}` : '');
      body.appendChild(meta);
      if (row.subject_type) {
        body.appendChild(el('p', 'hc-status', `Subject: ${row.subject_type} ${row.subject_id ?? ''}`));
      }
      const pre = el('pre', 'hc-json');
      pre.textContent = JSON.stringify(row.detail, null, 2);
      body.appendChild(pre);
      const actions = el('div', 'hc-dialog-actions');
      // Dismissing a read-only viewer is NEUTRAL — hc-btn-primary spent an
      // additive seat on "Close".
      const okBtn = mkIconBtn({ glyph: 'x', label: 'Close', attrs: { 'data-act': 'ok' } });
      okBtn.addEventListener('click', close);
      actions.appendChild(okBtn);
      body.appendChild(actions);
    });
  }

  async function loadActions(): Promise<void> {
    const { status: st, data } = await jsonFetch<{ actions: { action: string; n: number }[] }>(
      'GET', '/api/admin/audit/actions');
    if (st !== 200 || !data) return;
    actionOpts = [
      { value: '', label: 'All actions' },
      ...data.actions.map((a) => ({ value: a.action, label: `${a.action} (${a.n})` })),
    ];
    actionSel.set();
  }

  async function load(): Promise<void> {
    const params = new URLSearchParams();
    if (q.value.trim()) params.set('q', q.value.trim());
    if (actionSel.value) params.set('action', actionSel.value);
    if (sinceSel.value) {
      params.set('since', new Date(Date.now() - Number(sinceSel.value) * 3_600_000).toISOString());
    }
    params.set('limit', limitSel.value);

    status.textContent = 'loading…';
    const { status: st, data } = await jsonFetch<{ rows: AuditRow[]; count: number }>(
      'GET', `/api/admin/audit?${params.toString()}`);
    if (st === 401 || st === 403) return renderRefusal(root, st);
    rows = data?.rows ?? [];
    if (!grid) {
      const gridEl = root.querySelector('#hcGrid') as HTMLElement;
      currentDefs = buildAuditColumns(detailDialog);
      grid = makeGrid<AuditRow>(gridEl, {
        // The one decoration pass (R59a) — filters + pins over the defs.
        columnDefs: mach.decorate(currentDefs),
        rowData: rows,
        // Column changes persist into the SAME seat the filters ride.
        onColumnMoved: () => mach.persist(),
        onColumnResized: () => mach.persist(),
        onColumnVisible: () => mach.persist(),
      });
      void mach.wire(grid, {
        gridEl,
        colsBtn: toolbar.querySelector('#hcColsBtn'),
        colsMenu: toolbar.querySelector('#hcColsMenu'),
        resetBtn: toolbar.querySelector('#hcReset'),
      }).then((td) => { machTeardown = td; });
    } else {
      grid.setGridOption('rowData', rows);
    }
    paintCards();
    // Say when the view is CAPPED — a 200-row page that looks like the whole
    // history is how an audit conversation goes wrong.
    const capped = rows.length >= Number(limitSel.value);
    status.textContent = capped
      ? `${rows.length} rows (capped — narrow the filter to see more)`
      : `${rows.length} row${rows.length === 1 ? '' : 's'}`;
  }

  /** The phone card column (owner ruling 2026-09-06): each audit entry as a
   * stacked key:value card, titled by its action. Built only on a phone; the
   * display vocabulary is the SAME one the grid's filter/copy speak. */
  function paintCards(): void {
    if (!phone.isPhone()) { cardListEl.replaceChildren(); return; }
    renderCardList(cardListEl, gridRowsToCards(rows, {
      columnIds: AUDIT_COLUMN_IDS,
      headerName: (c) => currentDefs.find((d) => d.colId === c)?.headerName ?? c,
      displayValue: (c, r) => auditDisplayValue(currentDefs, c, r),
      titleCol: 'action',
    }), { empty: 'No audit entries for this filter.' });
  }
  const phone = watchPhone(() => paintCards());

  void loadActions();
  void load();
  // A dialog open across a route change would leak the engine's document
  // keydown listener — tear it down instantly before the shell goes. The
  // machinery's teardown joins it: the column picker's document click
  // dismiss would otherwise accumulate one copy per navigation.
  return () => {
    phone.teardown();
    machTeardown?.();
    destroyDialog(modal);
    shell.teardown();
  };
}
